// Controlling a device over BLE when there is no Wi-Fi.
//
// Lives apart from bleClient because it now needs an AUTHENTICATED session, and
// bleSession imports the transport — putting this back in bleClient would make
// the two import each other.
//
// A CONNECTION IS HELD OPEN across taps. Every command otherwise pays for a
// scan, a connect, an MTU negotiation and a three-message handshake — several
// seconds in front of every button press. The session is cached per unique
// number and reused until it drops.
//
// The device advertises `Smartify<UniqueNumber>` while the app knows it by its
// unique number, so finding one means scanning and matching the name suffix.

import { BleClient, ScanMode } from '@capacitor-community/bluetooth-le';
import {
  initBle, bleSupported, stopScan,
} from './bleClient';
import { BLE_NAME_PREFIX, SERVICE_UUID, SCAN_TIMEOUT_MS } from './bleConfig';
import { openSession } from './bleSession';

let link = null; // { uniqueNumber, session }

export const connectedUniqueNumber = () => (link ? link.uniqueNumber : null);

// Status notifications from whichever device is currently linked, so a physical
// press still reaches the UI while offline.
const statusListeners = new Set();
export function onBleStatus(fn) {
  statusListeners.add(fn);
  return () => statusListeners.delete(fn);
}

async function findByUniqueNumber(uniqueNumber) {
  const target = String(uniqueNumber).toUpperCase();
  let found = null;
  await initBle();
  await BleClient.requestLEScan({
    services: [SERVICE_UUID],
    allowDuplicates: false,
    // Active scan — the name is in the scan response, and matching by unique
    // number needs the name.
    scanMode: ScanMode.SCAN_MODE_LOW_LATENCY,
  }, (result) => {
    const name = (result.localName || result.device?.name || '').toUpperCase();
    if (!name.startsWith(BLE_NAME_PREFIX.toUpperCase())) return;
    if (name.includes(target) && !found) found = result.device.deviceId;
  });
  // Poll rather than always waiting the full window: a device that answers in
  // 200 ms should not cost the user 15 seconds.
  const deadline = Date.now() + SCAN_TIMEOUT_MS;
  while (!found && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => { setTimeout(r, 250); });
  }
  await stopScan();
  return found;
}

/**
 * An authenticated session for this device, reusing the open one if there is
 * one. Returns null when the device cannot be found or authenticated.
 */
export async function ensureControlLink(uniqueNumber, ownerSecret = null) {
  if (!bleSupported()) return null;
  const uno = String(uniqueNumber);

  if (link && link.uniqueNumber === uno && !link.session.isClosed()) return link.session;
  if (link) {
    await link.session.close();
    link = null;
  }

  const deviceId = await findByUniqueNumber(uno);
  if (!deviceId) return null;

  const opened = await openSession(deviceId, uno, {
    ownerSecret,
    onStatus: (text) => statusListeners.forEach((fn) => {
      try { fn(uno, text); } catch (e) { /* ignore listener error */ }
    }),
    onDisconnect: () => { link = null; },
  });
  if (!opened.ok) return null;

  link = { uniqueNumber: uno, session: opened.session };
  return opened.session;
}

/** Send a control command over BLE. Returns true if it went out. */
export async function sendControlOverBle(uniqueNumber, message, ownerSecret = null) {
  try {
    const session = await ensureControlLink(uniqueNumber, ownerSecret);
    if (!session) return false;
    await session.send(message);
    return true;
  } catch (e) {
    return false;
  }
}

/** Drop the held connection — call when leaving a device screen. */
export async function closeControlLink() {
  if (!link) return;
  await link.session.close();
  link = null;
}
